/**
 * Revision files for playback and download.
 *
 * The revision endpoint (`/api/project/:id/revisions/:rev/download`) only
 * accepts an `Authorization: Bearer` header, which a <video src> cannot send,
 * so a cut can't be streamed: the whole file is fetched and played from a
 * blob URL. XHR rather than fetch so the browser stores the body as a blob
 * (not in the JS heap) and reports progress for the loading bar.
 */

export interface RevisionFileProgress {
  loaded: number;
  /** Null when the server sends no length. */
  total: number | null;
}

export interface RevisionFileRequest {
  promise: Promise<Blob>;
  abort: () => void;
}

export class RevisionFileError extends Error {
  status: number;

  constructor(status: number) {
    super(
      status === 401 || status === 403
        ? 'You don’t have access to this file. Sign in again and retry.'
        : status === 404
          ? 'This file is no longer available.'
          : 'The file could not be loaded. Check your connection and try again.'
    );
    this.name = 'RevisionFileError';
    this.status = status;
  }
}

const abortError = () => {
  const error = new Error('Loading was cancelled.');
  error.name = 'AbortError';
  return error;
};

export const requestRevisionFile = (
  url: string,
  token: string,
  onProgress?: (progress: RevisionFileProgress) => void
): RevisionFileRequest => {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<Blob>((resolve, reject) => {
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onprogress = (event) => {
      onProgress?.({ loaded: event.loaded, total: event.lengthComputable && event.total > 0 ? event.total : null });
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300 && xhr.response) resolve(xhr.response as Blob);
      else reject(new RevisionFileError(xhr.status));
    };
    xhr.onerror = () => reject(new RevisionFileError(0));
    xhr.onabort = () => reject(abortError());
    xhr.send();
  });
  return { promise, abort: () => xhr.abort() };
};

/** Save a URL the browser already holds (a blob URL) under `filename`. */
export const saveFile = (url: string, filename: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
